import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactCountryFlag from "react-country-flag";
import PropTypes from "prop-types";
import $ from "jquery";
import {
  Stack,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Skeleton,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import { EventsTimeRangePicker } from "../../../common/Greens";
import { StyledMenu } from "../../application/analytics/styled";
import { PeriodItemComponent } from "../../application/analytics/common";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import SiteScopeGate from "../../../common/SiteScopeGate";
import PermissionGate from "../../../guards/PermissionGate";
import { formatDate } from "../../../../utils/format";

import useEvent from "../../../../hooks/user/useEvent";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import useHome from "../../../../hooks/user/useHome";
import useSite from "../../../../hooks/user/useSite";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import EventFilterBar from "./EventFilterBar";
import SmartParseEventDrawer from "./SmartParseEventDrawer";
import SignatureWafEventDrawer from "./SignatureWafEventDrawer";
import WAFEventsHeatmap from "./WAFEventsHeatmap";
import WAFEventOverviewStrip from "../services/web_application/components/WAFDashboardOverviewStrip";
import {
  getWafEventStatus,
  getWafEventDetectionDisplay,
  getWafEventRowKey,
  getWafEventListSource,
  buildCustomWafTimeRange,
  normalizeWafEventTimeRange,
} from "./wafEventListUtils";
import { getDefaultEventListingCustomDateRange } from "./eventTimeRange";
import { readDashboardEventDeepLinkState } from "./dashboardEventNavigation";
import { buildConditionsFromFilters, getActionFromFilters, withSearchCondition } from "./eventFilterConditions";
import { WafAction } from "../../../../utils/constants";
import { isV3Envelope } from "../../../../utils/wafEventV3";
import IntentPill from "./v3/IntentPill";
import BurstBadge from "./v3/BurstBadge";
import BurstListModal from "./v3/BurstListModal";

const SEARCH_DEBOUNCE_MS = 500;

function readDeepLinkSearchQuery() {
  try {
    return new URLSearchParams(window.location.search).get("q") || "";
  } catch (_) {
    return "";
  }
}
export const tableHeadSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
export const tableBodySx =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] };

export const getStatusColor = (status) => {
  switch (status) {
    case "Blocked":
      return colors.red[100];
    case "Challenged":
      return colors.yellow[100];
    case "Passed":
      return colors.green[300];
    default:
      return colors.neutral[800];
  }
};

export const getSeverityConfig = (severity) => {
  switch (severity?.toUpperCase()) {
    case "S1":
    case "S1CRITICAL":
    case "CRITICAL":
      return { label: "Critical", color: "#B91C1C", bgColor: "#FEE2E2" };
    case "S2":
    case "S2HIGH":
    case "HIGH":
      return { label: "High", color: "#C2410C", bgColor: "#FFEDD5" };
    case "S3":
    case "S3MEDIUM":
    case "MEDIUM":
      return { label: "Medium", color: "#B45309", bgColor: "#FEF3C7" };
    case "S4":
    case "S4LOW":
    case "LOW":
      return { label: "Low", color: "#15803D", bgColor: "#DCFCE7" };
    default:
      return { label: severity || "—", color: colors.neutral[700], bgColor: colors.neutral[100] };
  }
};

function SkeletonContent(props) {
  const { rowsPerPage, columnCount = 6 } = props;

  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  var cellArray = Array.from({ length: columnCount }, (_, i) => i);

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{
                padding: "16px 8px",
              }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{
                  borderRadius: "11px",
                }}
              />
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      ))}
    </TableBody>
  );
}
SkeletonContent.propTypes = {
  rowsPerPage: PropTypes.number.isRequired,
  columnCount: PropTypes.number,
};

// Shown when the user landed via global-search deep-link (`?q=foo`).
// The search box is seeded from `q` and filters via the backend; this
// banner only explains the deep-link origin.
const DeepLinkSearchBanner = ({ q, onDismiss }) => {
  if (!q) return null;
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      backgroundColor: "#EEF0FE", border: "1px solid #C8D0FA", borderRadius: "8px",
      px: 2, py: 1.25, mb: 2,
    }}>
      <Typography sx={ { fontSize: 13, color: colors.neutral[1000], flex: 1 }}>
        Searching for <Box component="span" sx={{ fontWeight: 600 }}>"{q}"</Box> from global search.
      </Typography>
      <IconButton size="small" onClick={() => {
        const u = new URL(window.location.href); u.searchParams.delete("q");
        window.history.replaceState({}, "", u.toString());
        onDismiss?.();
      }}>
        <CloseIcon style={{ width: 14, height: 14, color: colors.neutral[600] }} />
      </IconButton>
    </Box>
  );
};

const WAFEventPage = () => {
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
    setWafEventController,
  } = useAuth();
  const { PERMISSIONS } = usePermissions();
  const {
    time_range,
    smartParseEvents,
    total_smartparse_events_count,
    getSmartParseEvents,
    getOnlySmartParseEvents,
    smartParseEventLoading,
  } = useEvent();
  const { curProjectId } = useHome();
  const { siteList, getSitesForItems } = useSite();
  const { apiSiteId: siteID } = useGlobalSite();
  const location = useLocation();
  const navigate = useNavigate();
  const dashboardDeepLink = useMemo(() => readDashboardEventDeepLinkState(location.state), [location.state]);

  useEffect(() => {
    if (isAuthenticated && !siteList) getSitesForItems();
  }, [isAuthenticated, siteList, getSitesForItems]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSource, setDrawerSource] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeFilters, setActiveFilters] = useState(() => dashboardDeepLink?.initialFilters ?? []);
  const [search, setSearch] = useState(() => readDeepLinkSearchQuery());
  const [query, setQuery] = useState(() => readDeepLinkSearchQuery().trim());
  const [showDeepLinkBanner, setShowDeepLinkBanner] = useState(() => Boolean(readDeepLinkSearchQuery()));
  // Tier-3 burst-list modal state
  const [burstModalOpen, setBurstModalOpen] = useState(false);
  const [burstModalContext, setBurstModalContext] = useState(null);

  useEffect(() => {
    const h = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [search]);

  const handleSearchChange = (value) => {
    setSearch(value);
    if (!value) setShowDeepLinkBanner(false);
  };

  const handleBurstBadgeClick = (item, correlation) => {
    if (!isV3Envelope(item) || !correlation || !correlation.group_id) return;
    setBurstModalContext({
      correlation,
      tuple: {
        src_ip:      item?.attacker?.source_ip || item?.attacker?.ip,
        intent_name: item?.primary_intent?.intent_name,
        host:        item?.target?.host,
        path:        item?.target?.uri || item?.target?.path,
      },
    });
    setBurstModalOpen(true);
  };
  const handleBurstModalClose = () => {
    setBurstModalOpen(false);
    setBurstModalContext(null);
  };
  const handleBurstEventClick = (ev) => {
    handleBurstModalClose();
    if (!ev || !ev.event_id) return;
    setSelectedEvent({
      schema_version: "layerd.waf.event.v3",
      event_id: ev.event_id,
      request_id: ev.event_id,
    });
    setDrawerSource("smart_parse");
    setDrawerOpen(true);
  };

  const handleFiltersChange = (newFilters) => {
    // Only update if filters actually changed
    const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(activeFilters);
    if (filtersChanged) {
      setActiveFilters(newFilters);
    }
  };

  const handleEventClick = (eventItem) => {
    setSelectedEvent(eventItem);
    setDrawerSource(getWafEventListSource(eventItem));
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerSource(null);
    setSelectedEvent(null);
  };

  const handleFilterSimilar = (filterType, event) => {
    if (!event) return;
    
    let filterId = null;
    let filterValue = null;
    let filterLabel = null;
    
    switch (filterType) {
      case "ip":
        filterId = "src_ip";
        filterValue = event.ip;
        filterLabel = "IP Address";
        break;
      case "detection_type":
        filterId = "action";
        filterValue = event.action_taken || null;
        filterLabel = "Detection Type";
        break;
      case "country":
        filterId = "country";
        filterValue = event.country;
        filterLabel = "Country";
        break;
      case "status":
        filterId = "status";
        filterValue = getWafEventStatus(event);
        filterLabel = "Status";
        break;
      default:
        return;
    }
    
    if (!filterId || !filterValue) return;
    
    // Check if filter already exists
    const existingFilterIndex = activeFilters.findIndex((f) => f.id === filterId);
    
    if (existingFilterIndex >= 0) {
      // Update existing filter
      const newFilters = [...activeFilters];
      newFilters[existingFilterIndex] = {
        ...newFilters[existingFilterIndex],
        value: filterValue,
      };
      handleFiltersChange(newFilters);
    } else {
      // Add new filter
      const newFilters = [
        ...activeFilters,
        {
          id: filterId,
          label: filterLabel,
          value: filterValue,
        },
      ];
      handleFiltersChange(newFilters);
    }
  };

  const [timeRange, setTimeRange] = React.useState(() => {
    if (dashboardDeepLink?.timeRange) return dashboardDeepLink.timeRange;
    if (time_range?.period) return time_range.period;
    if (time_range?.from && time_range?.to) return "custom";
    return "custom";
  });
  const [customRangeConfirm, setCustomRangeConfirm] = React.useState(
    () => dashboardDeepLink?.customRangeConfirm ?? true
  );

  const [anchorEl, setAnchorEl] = React.useState(null);

  const [customDateRange, setCustomDateRange] = React.useState(() => {
    if (dashboardDeepLink?.customDateRange) return dashboardDeepLink.customDateRange;
    return getDefaultEventListingCustomDateRange();
  });

  useEffect(() => {
    if (!dashboardDeepLink) return;

    if (dashboardDeepLink.initialFilters?.length) {
      setActiveFilters(dashboardDeepLink.initialFilters);
    }
    if (dashboardDeepLink.timeRange === "custom" && dashboardDeepLink.customDateRange) {
      setTimeRange("custom");
      setCustomDateRange(dashboardDeepLink.customDateRange);
      setCustomRangeConfirm(dashboardDeepLink.customRangeConfirm ?? true);
    } else if (dashboardDeepLink.timeRange && dashboardDeepLink.timeRange !== "custom") {
      setTimeRange(dashboardDeepLink.timeRange);
      setCustomDateRange(getDefaultEventListingCustomDateRange());
      setCustomRangeConfirm(false);
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [dashboardDeepLink, location.pathname, navigate]);

  const getTimeRange = () => {
    if (timeRange === "custom") {
      return buildCustomWafTimeRange(customDateRange[0], customDateRange[1]);
    }
    return normalizeWafEventTimeRange({ period: timeRange || time_range?.period || "7d" });
  };
  const selectPeriod = (period) => {
    setAnchorEl(null);
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
          "z-index": 1200,
        });
      });
    }
  };
  const selectCustomDateRange = (res) => {
    setCustomRangeConfirm(true);
    setCustomDateRange(res);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getEventsWithFillter = async () => {
    if (homeController) homeController.abort();
    if (wafdashController) wafdashController.abort();
    if (websiteController) websiteController.abort();
    if (wafeventController) wafeventController.abort();
    if (planController) planController.abort();
    
    const _conditions = withSearchCondition(
      buildConditionsFromFilters(activeFilters, "waf"),
      query
    );
    const _action = getActionFromFilters(activeFilters);
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;

    getSmartParseEvents(setWafEventController, siteID, getTimeRange(), ITEMS_PER_PAGE, 0, _action, _conditions);
  };

  // Memoize filters string to prevent unnecessary API calls
  const filtersKey = useMemo(() => {
    return JSON.stringify({
      filters: activeFilters.map(f => ({ id: f.id, value: f.value })).sort((a, b) => a.id.localeCompare(b.id)),
      q: query,
    });
  }, [activeFilters, query]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (timeRange === "custom" && customRangeConfirm === false) {
      return;
    }
    setCustomRangeConfirm(false);
    getEventsWithFillter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, siteID, customDateRange, timeRange, filtersKey, curProjectId]);

  const [allEvents, setAllEvents] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const currentConditionsRef = useRef([]);
  const currentActionRef = useRef(WafAction.ALL);
  const ITEMS_PER_PAGE = 10;

  // Memoize conditions to prevent unnecessary re-renders
  const currentConditions = useMemo(() => {
    return withSearchCondition(buildConditionsFromFilters(activeFilters, "waf"), query);
  }, [activeFilters, query]);

  const currentAction = useMemo(() => {
    return getActionFromFilters(activeFilters);
  }, [activeFilters]);

  const heatmapTimeRange = useMemo(() => {
    if (timeRange === "custom" && customRangeConfirm === false) return null;
    return getTimeRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customDateRange, customRangeConfirm]);

  // Update ref when conditions change
  React.useEffect(() => {
    currentConditionsRef.current = currentConditions;
    currentActionRef.current = currentAction;
  }, [currentConditions, currentAction]);

  const currentEvents = smartParseEvents;
  const currentTotalCount = total_smartparse_events_count;
  const getSafeHasMore = (loadedCount, latestBatchLength, totalCount) => {
    const normalizedTotal = Number(totalCount);
    if (Number.isFinite(normalizedTotal) && normalizedTotal > 0) {
      return loadedCount < normalizedTotal;
    }
    return latestBatchLength >= ITEMS_PER_PAGE;
  };

  React.useEffect(() => {
    if (currentEvents !== null) {
      if (currentEvents.length === 0 && allEvents.length > 0 && isInitialLoadRef.current) {
        setAllEvents([]);
        setHasMore(true);
        isInitialLoadRef.current = true;
      } else if (currentEvents.length > 0) {
        if (isInitialLoadRef.current) {
          const initialEvents = currentEvents.slice(0, ITEMS_PER_PAGE);
          setAllEvents(initialEvents);
          setHasMore(getSafeHasMore(initialEvents.length, currentEvents.length, currentTotalCount));
          isInitialLoadRef.current = false;
        } else {
          setAllEvents((prev) => {
            const existingIds = new Set(prev.map((e) => e.request_id).filter(Boolean));
            const newEventsToAdd = currentEvents
              .slice(0, ITEMS_PER_PAGE)
              .filter((e) => e.request_id && !existingIds.has(e.request_id));
            if (newEventsToAdd.length === 0) {
              setHasMore(getSafeHasMore(prev.length, currentEvents.length, currentTotalCount));
              return prev;
            }
            const newEvents = [...prev, ...newEventsToAdd];
            setHasMore(getSafeHasMore(newEvents.length, currentEvents.length, currentTotalCount));
            return newEvents;
          });
        }
      }
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvents, currentTotalCount]);

  React.useEffect(() => {
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteID, customDateRange, timeRange, curProjectId, filtersKey]);

  React.useEffect(() => {
    if (!hasMore || isLoadingMore || allEvents.length < ITEMS_PER_PAGE) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const perfectScrollbar = document.querySelector(".ps");
    const root = perfectScrollbar || null;
    const rootMargin = "200px";

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          if (allEvents.length >= ITEMS_PER_PAGE && allEvents.length < currentTotalCount) {
            setIsLoadingMore(true);
            const nextFrom = allEvents.length;
            const currentTimeRange = getTimeRange();
            getOnlySmartParseEvents(
              setWafEventController,
              siteID,
              currentTimeRange,
              ITEMS_PER_PAGE,
              nextFrom,
              currentActionRef.current,
              currentConditionsRef.current
            );
          }
        }
      },
      {
        root: root,
        rootMargin: rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMore,
    isLoadingMore,
    allEvents.length,
    siteID,
    timeRange,
    customDateRange,
    filtersKey,
    getOnlySmartParseEvents,
    setWafEventController,
    currentTotalCount,
  ]);

  return (
    <PermissionGate permission={PERMISSIONS.WAF_READ} resourceLabel="WAF events">
    <>
      <Helmet title="WAF Event" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <V2PageHeader title="WAF Event" sx={{ pb: 0 }} />
        <SiteScopeGate mode="any">
          {() => (
            <>
        <Stack spacing={2}>
          <EventFilterBar
            eventType="waf"
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            events={allEvents}
            search={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search host, URI, IP, UA…"
            searchPending={search.trim() !== query}
            timeRangeComponent={
              <EventsTimeRangePicker
                selectPeriod={selectPeriod}
                selectCustomDateRange={selectCustomDateRange}
                timeRange={timeRange}
                customDateRange={customDateRange}
              />
            }
            onRefresh={getEventsWithFillter}
            refreshing={smartParseEventLoading}
          />
          {showDeepLinkBanner ? (
            <DeepLinkSearchBanner
              q={query || search}
              onDismiss={() => setShowDeepLinkBanner(false)}
            />
          ) : null}
        </Stack>
        <WAFEventOverviewStrip
          siteId={siteID}
          timeRange={heatmapTimeRange}
          conditions={currentConditions}
          customDateRange={customDateRange}
        />
        {/* Activity heatmap — same site, time range, and pill filters as the table. */}
        <WAFEventsHeatmap
          siteId={siteID}
          timeRange={heatmapTimeRange}
          conditions={currentConditions}
          action={currentAction}
          customDateRange={customDateRange}
          exactSeries
        />
        <Stack>
          <Box ref={tableContainerRef} spacing={6} sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px", p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 3, px: 3 },
                "& .MuiTableCell-body": { py: 2, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Typography sx={tableHeadSx}>Event ID</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Hostname</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Activity</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>IP address</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Type of detection</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Status</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 230 }}>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 72 }}>
                    <Typography sx={tableHeadSx}>Action</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {currentEvents === null && allEvents.length === 0 ? (
                <SkeletonContent rowsPerPage={ITEMS_PER_PAGE} columnCount={7} />
              ) : allEvents.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography textAlign="center" sx={tableBodySx}>
                        There are no events
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {allEvents?.map((item, index) => {
                    const isV3Row = isV3Envelope(item);
                    const v3Severity = isV3Row ? item?.assessment?.severity : null;
                    const hostName = isV3Row ? item?.target?.host : item?.host;
                    const method = isV3Row ? item?.target?.method : item?.method;
                    const uri = isV3Row ? (item?.target?.uri || item?.target?.path) : item?.uri;
                    const sourceIp = isV3Row ? (item?.attacker?.source_ip || item?.attacker?.ip) : item?.ip;
                    const countryIso = isV3Row ? item?.attacker?.geo?.country_iso : item?.country;
                    const timestamp = isV3Row ? item?.detected_at : item?.ts;
                    const eventId = isV3Row ? item?.event_id : item?.request_id;
                    const status = getWafEventStatus(item);
                    const detectionDisplay = getWafEventDetectionDisplay(item);
                    return (
                      <TableRow
                        key={getWafEventRowKey(item, index)}
                        onClick={() => handleEventClick(item)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: colors.primary[50],
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              ...tableBodySx,
                              fontFamily: "monospace",
                              color: "#1E40AF",
                              wordBreak: "break-all",
                            }}
                          >
                            {eventId || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={tableBodySx}>{hostName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography sx={tableBodySx}>
                                {method}
                              </Typography>
                              {isV3Row && (
                                <BurstBadge
                                  correlation={item?.correlation}
                                  onClick={(c) => handleBurstBadgeClick(item, c)}
                                />
                              )}
                            </Stack>
                            {uri?.length > 36 ? (
                              <Tooltip title={uri} arrow>
                                <Typography sx={ { fontSize: 11, color: "#1E40AF", lineHeight: "14px" }}>{uri?.substr(0, 36) + " ..."}</Typography>
                              </Tooltip>
                            ) : (
                              <Typography sx={ { fontSize: 11, color: "#1E40AF", lineHeight: "14px" }}>{uri}</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems={"center"}>
                            {countryIso ? (
                              <ReactCountryFlag
                                countryCode={countryIso}
                                svg
                                title={countryIso}
                                style={{
                                  width: "26px",
                                  height: "18px",
                                }}
                              />
                            ) : null}
                            <Typography sx={{ ...tableBodySx, pl: 2 }}>{sourceIp}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {isV3Row ? (
                            <Stack spacing={0.5} alignItems="flex-start">
                              <IntentPill
                                primaryIntent={item?.primary_intent}
                                severity={v3Severity}
                                cweId={item?.primary_intent?.classification?.cwe_id}
                                compact
                              />
                              {Array.isArray(item?.intents) && item.intents.length > 1 ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: 11,
                                    lineHeight: "16px",
                                    fontWeight: 400,
                                    color: colors.neutral[600],
                                  }}
                                >
                                  +{item.intents.length - 1} more intent{item.intents.length - 1 === 1 ? "" : "s"}
                                </Typography>
                              ) : null}
                            </Stack>
                          ) : (
                            <Stack spacing={0.25}>
                              <Typography
                                variant="body1"
                                sx={
                                  detectionDisplay.mode === "empty"
                                    ? { ...tableBodySx, color: colors.neutral[500] }
                                    : { ...tableBodySx, fontWeight: 400, color: colors.neutral[1000] }
                                }
                              >
                                {detectionDisplay.title}
                              </Typography>
                              {detectionDisplay.subtitle ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: 12,
                                    lineHeight: "18px",
                                    fontWeight: 400,
                                    color: colors.primary[300],
                                  }}
                                >
                                  {detectionDisplay.subtitle}
                                </Typography>
                              ) : null}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction={"row"} alignItems={"center"} spacing={2}>
                            <Box
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: getStatusColor(status),
                                borderRadius: 100,
                              }}
                            />
                            <Typography sx={tableBodySx}>{status}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography sx={tableBodySx}>{formatDate(timestamp) || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
            {hasMore && allEvents.length >= ITEMS_PER_PAGE && <Box ref={sentinelRef} sx={{ height: 1, width: "100%" }} />}
            {isLoadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!hasMore && allEvents.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>No more events to load</Typography>
              </Box>
            )}
          </Box>
        </Stack>
            </>
          )}
        </SiteScopeGate>
      </Stack>
      <StyledMenu
        MenuListProps={{ "aria-labelledby": "demo-customized-button" }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <PeriodItemComponent selectPeriod={selectPeriod} />
      </StyledMenu>
      {drawerSource === "smart_parse" && (
        <SmartParseEventDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          event={selectedEvent}
        />
      )}
      {drawerSource === "signature_waf" && (
        <SignatureWafEventDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          requestId={selectedEvent?.request_id}
          event={selectedEvent}
        />
      )}
      {burstModalContext && (
        <BurstListModal
          open={burstModalOpen}
          onClose={handleBurstModalClose}
          correlation={burstModalContext.correlation}
          tuple={burstModalContext.tuple}
          onEventClick={handleBurstEventClick}
        />
      )}
    </>
    </PermissionGate>
  );
};
export default WAFEventPage;
